import React, { useEffect, useMemo } from 'react'
import MatchedCard from '../MatchedCard'
import { ToolComponentCtx } from '@hose/chat-app'
import { IFeeTypeMatchedItem, FeeType, ISpecificationMatched } from '../../types/dataMatched'
import { openFeeTypeModal, showDialog} from '../../utils/postmate'
import { useConfig, useFeeTypeSelection } from '../../hooks/useChatInitialization'
import { useMatchedData } from '../../hooks/useMatchedData'
import { Modal } from '@hose/eui'
import { v1 as uuid } from 'uuid';
import { getParamFromUrl } from '../../utils/tools'


export const FeeTypeMatched: React.FC<ToolComponentCtx> = (props) => {
  const { state, toolCallId } = props

  const config = useConfig();
  const { feeTypesData } = config;

  const isMobile = useMemo(() => {
		return getParamFromUrl('platform') === 'mobile'
	}, [])

  // 使用通用的匹配数据管理 hook
  const {
    matchedData,
    setMatchedData,
    onReset,
    onConfirm,
    isHidden,
    countdown,
    isCountdown,
    cancelCountdown
  } = useMatchedData<IFeeTypeMatchedItem>(props, {
    defaultData: [],
    enableAutoSubmit: true, // 启用倒计时自动提交
    countdownSeconds: 10, // 10秒倒计时
    hideAfterSubmit: true, // 提交后隐藏组件
    enableCache: true, // 启用缓存功能
    parseData: (result) => {
      const res =  result.map((item:IFeeTypeMatchedItem) => {
        const feeTypes = item.feeTypes.map((item:FeeType) => {
          const curFeeType = feeTypesData?.map?.[item.id]
          return {
            ...item,
            color: curFeeType?.color,
            icon: curFeeType?.icon
          }
        })
        return {
          ...item,
          feeTypes
        }
      })
      return res
		}
  })


  // 使用新的 hook 监听费用类型选择事件
  const selectedFeeType = useFeeTypeSelection();

  // 处理选中的费用类型数据
  useEffect(() => {
    if (selectedFeeType && selectedFeeType?.toolCallId === toolCallId) {
      console.log('📥 收到选中的费用类型:', selectedFeeType);

      // 如果是新增费用类型
      if (selectedFeeType.isNew) {
        setMatchedData(prev => {
          // 添加新费用类型到指定 tabItem 的 feeTypes 数组
          const newFeeType: FeeType = {
            uuid: selectedFeeType.uuid || `uuid_${uuid()}`,
            id: selectedFeeType.id || `feeType_${uuid()}`,
            color: selectedFeeType.color,
            icon: selectedFeeType.icon,
            name: selectedFeeType.name || '新费用类型',
            attachments: []
          };

          const result = prev.map(item => {
            if (item.uuid === selectedFeeType.tabItem?.uuid) {
              return {
                ...item,
                feeTypes: [...item.feeTypes, newFeeType]
              }
            }
            return item
          })
          return result
        });
      } else {
        // 如果是切换费用类型，需要合并附件
        setMatchedData(prev => {
          const result = prev.map(item => {
            if (item.uuid === selectedFeeType.tabItem?.uuid) {
              return {
                ...item,
                feeTypes: item.feeTypes.map(feeType => {
                  if (feeType.uuid === selectedFeeType.originalUuid) {
                    return {
                      ...feeType,
                      icon: selectedFeeType.icon,
                      color: selectedFeeType.color,
                      id: selectedFeeType.id,
                      name: selectedFeeType.name,
                    }
                  }
                  return feeType
                })
              }
            }
            return item
          })
          return result
        });
      }
    }
  }, [selectedFeeType, toolCallId, setMatchedData]);

  if (state !== 'result') {
    return null
  }

  // 如果组件被隐藏，返回 null
  if (isHidden) {
    return null
  }

  if (state === 'result' && !props?.result) {
    return null
  }

  const handleAttachmentMove = (attachmentId: string, fromItemId: string, toItemId: string, insertIndex: number = 0) => {
    console.log('附件移动:', { attachmentId, fromItemId, toItemId, insertIndex })

    let attachmentToMove: any = null
    let sourceFeeTypeId: string | null = null
    let tabItemId: string | null = null

    for (const tabItem of matchedData) {
      for (const feeType of tabItem.feeTypes) {
        const attachment = feeType.attachments.find(att => att.fileKey === attachmentId)
        if (attachment) {
          attachmentToMove = attachment
          sourceFeeTypeId = feeType.uuid
          tabItemId = tabItem.uuid
          break
        }
      }
      if (attachmentToMove) break
    }
    if (!attachmentToMove) {
      for (const tabItem of matchedData) {
        const attachment = tabItem.unassignedFiles?.find(att => att.fileKey === attachmentId)
        if (attachment) {
          attachmentToMove = attachment
          sourceFeeTypeId = null
          tabItemId = tabItem.uuid
          break
        }
      }
    }
    if (!attachmentToMove) {
      console.log('未找到要移动的附件:', attachmentId)
      return
    }

    const isFromUnassigned = fromItemId.startsWith('unassigned-') || sourceFeeTypeId === null
    const isToUnassigned = toItemId.startsWith('unassigned-')


    let targetFeeTypeId: string | null = null

    if (isToUnassigned) {
      targetFeeTypeId = null
    } else {
      for (const tabItem of matchedData) {
        for (const feeType of tabItem.feeTypes) {
          if (feeType.uuid === toItemId) {
            targetFeeTypeId = feeType.uuid
            break
          }
        }
        if (targetFeeTypeId) break
      }
    }

    setMatchedData(prev => {
      if ((sourceFeeTypeId === targetFeeTypeId) && ((isFromUnassigned && isToUnassigned) || (!isFromUnassigned && !isToUnassigned))) {
        // 同容器内排序（同费类型或同未匹配区）
        if (isFromUnassigned) {
          return prev.map(tabItem =>
            tabItem.uuid === tabItemId
              ? {
                ...tabItem,
                unassignedFiles: (() => {
                  const list = [...(tabItem.unassignedFiles || [])]
                  const oldIndex = list.findIndex(att => att.fileKey === attachmentId)
                  list.splice(oldIndex, 1)
                  list.splice(insertIndex, 0, attachmentToMove)
                  return list
                })()
              }
              : tabItem
          )
        } else {
          const result = prev.map(tabItem => {
            const tab = tabItem.uuid === tabItemId
              ? {
                ...tabItem,
                feeTypes: tabItem.feeTypes.map(feeType => {
                  const res = feeType.uuid === sourceFeeTypeId
                    ? {
                      ...feeType,
                      attachments: (() => {
                        const list = [...feeType.attachments]
                        const oldIndex = list.findIndex(att => att.fileKey === attachmentId)
                        list.splice(oldIndex, 1)
                        list.splice(insertIndex, 0, attachmentToMove)
                        return list
                      })()
                    }
                    : feeType
                  return res
                })
              }
              : tabItem
            console.log(tab, 'tab')
            return tab
          })
          console.log(result, 'result')
          return result
        }
      } else {
        // 跨容器移动：从源移除，再添加到目标
        let newMatchedData = prev.map(tabItem => {
          if (tabItem.uuid === tabItemId) {
            if (isFromUnassigned) {
              return { ...tabItem, unassignedFiles: (tabItem.unassignedFiles || []).filter(att => att.fileKey !== attachmentId) }
            } else {
              return {
                ...tabItem,
                feeTypes: tabItem.feeTypes.map(feeType =>
                  feeType.uuid === sourceFeeTypeId
                    ? { ...feeType, attachments: feeType.attachments.filter(att => att.fileKey !== attachmentId) }
                    : feeType
                )
              }
            }
          }
          return tabItem
        })

        newMatchedData = newMatchedData.map(tabItem => {
          if (tabItem.uuid === tabItemId) {
            if (isToUnassigned) {
              const list = [...(tabItem.unassignedFiles || [])]
              list.splice(insertIndex, 0, attachmentToMove)
              return { ...tabItem, unassignedFiles: list }
            } else {
              return {
                ...tabItem,
                feeTypes: tabItem.feeTypes.map(feeType => {
                  const feeTypeResult = feeType.uuid === targetFeeTypeId
                    ? {
                      ...feeType,
                      attachments: (() => {
                        const list = [...feeType.attachments]
                        list.splice(insertIndex, 0, attachmentToMove)
                        return list
                      })()
                    }
                    : feeType

                  return feeTypeResult
                }

                )
              }
            }
          }
          return tabItem
        })
        console.log(newMatchedData)
        return newMatchedData
      }
    })

    console.log('附件移动完成')
  }

  const onDelete = (dataSource: FeeType | ISpecificationMatched, tabItem?: IFeeTypeMatchedItem) => {
    const onOk = () => {
      setMatchedData(prev => {
        return prev.map(item => {
          if (item.uuid === tabItem?.uuid) {
            return {
              ...item,
              feeTypes: item.feeTypes.filter(feeType => feeType.id !== dataSource.id),
              unassignedFiles: [...(item.unassignedFiles || []), ...dataSource.attachments]
            }
          }
          return item
        })
      })
    }
    const title = '确定删除费用类型分组'
    const content = '删除后，其下所有文件将自动移动到“未匹配文件”区域'
    if (dataSource.attachments.length > 0) {
      if(isMobile) {
        showDialog({
          type: 'confirm',
          title,
          content,
          onConfirm: onOk
        })
      } else {
        Modal.confirm({
          title,
          content,
          onOk
        })
      }
    } else {
      onOk()
    }

  }

  const handleAdd = async (tabItem?: IFeeTypeMatchedItem) => {
    console.log('新增费用类型')
    try {
      await openFeeTypeModal({
        toolCallId,
        tabItem,
        isNew: true,
        action: 'add'
      })
    } catch (error) {
      console.error('新增费用类型失败:', error)
    }
  }

  const onChange = async (dataSource: FeeType | ISpecificationMatched, tabItem?: IFeeTypeMatchedItem) => {
    console.log('切换费用类型')
    try {
      await openFeeTypeModal({
        toolCallId,
        dataSource,
        tabItem,
        isNew: false,
        action: 'change',
        originalUuid: dataSource.uuid
      })
    } catch (error) {
      console.error('切换费用类型失败:', error)
    }
  }

  // 避免在渲染期间频繁日志导致控制台刷屏
  return (
    <MatchedCard
      cardTitle="确认费用类型"
      matchedData={matchedData}
      addAssignedText="新增费用类型"
      enableDrag={true}
      isCountdown={isCountdown}
      countdown={countdown}
      onCancelCountdown={cancelCountdown}
      onAttachmentMove={handleAttachmentMove}
      onAdd={handleAdd}
      onDelete={onDelete}
      onChange={onChange}
      onConfirm={onConfirm}
      onReset={onReset}
    />
  )
}

export default FeeTypeMatched